<template>
  <div>
    <p class="tw-text-muted tw-mb-4 tw-text-sm">
      MLS boards Brokurz shows in <strong>Add MLS</strong> come from this warehouse
      <code>feeds</code> list. Add / edit / pause here — Brokurz picks them up with no deploy.
    </p>

    <Message v-if="!warehouseConfigured" severity="warn" :closable="false" class="tw-mb-4">
      Set <code>LISTINGS_SUPABASE_URL</code> and <code>LISTINGS_SUPABASE_SERVICE_ROLE_KEY</code>
      in <code>.env</code> (listings Supabase project), then restart the API.
    </Message>

    <div class="kpi-row tw-mb-4">
      <div class="kpi-card">
        <div class="kpi-label">Showing</div>
        <div class="kpi-value">{{ feeds.length }}</div>
        <div class="kpi-hint">of {{ allFeeds.length }} boards</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-label">Active</div>
        <div class="kpi-value">{{ countByStatus('active') }}</div>
        <div class="kpi-hint">Live sync eligible</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-label">Draft</div>
        <div class="kpi-value">{{ countByStatus('draft') }}</div>
        <div class="kpi-hint">Not shown in Brokurz until active</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-label">Paused / retired</div>
        <div class="kpi-value">{{ countByStatus('paused') + countByStatus('retired') }}</div>
        <div class="kpi-hint">Hidden from Brokurz picker</div>
      </div>
    </div>

    <Card class="tw-mb-4">
      <template #title>{{ editingId ? 'Edit board' : 'Add board' }}</template>
      <template #subtitle>
        Brokurz lists <code>active</code> boards only (not draft / paused / demo / retired).
        Pending broker requests appear under <router-link to="/applications">Applications</router-link>.
      </template>
      <template #content>
        <form class="board-form" @submit.prevent="save">
          <div class="board-grid">
            <div>
              <label class="field-label">Display name</label>
              <InputText v-model="form.displayName" class="tw-w-full" required placeholder="CRMLS (Trestle)" />
            </div>
            <div>
              <label class="field-label">Slug</label>
              <InputText
                v-model="form.slug"
                class="tw-w-full"
                :required="!editingId"
                placeholder="crmls-trestle"
                :disabled="Boolean(editingId)"
              />
            </div>
            <div>
              <label class="field-label">Board name</label>
              <InputText v-model="form.mlsBoardName" class="tw-w-full" placeholder="California Regional MLS" />
            </div>
            <div>
              <label class="field-label">Acronym</label>
              <InputText v-model="form.mlsBoardAcronym" class="tw-w-full" placeholder="CRMLS" />
            </div>
            <div>
              <label class="field-label">Vendor</label>
              <Dropdown
                v-model="form.vendor"
                :options="vendorOptions"
                optionLabel="label"
                optionValue="value"
                class="tw-w-full"
              />
            </div>
            <div>
              <label class="field-label">Status</label>
              <Dropdown
                v-model="form.status"
                :options="statusOptions"
                optionLabel="label"
                optionValue="value"
                class="tw-w-full"
              />
            </div>
            <div>
              <label class="field-label">State</label>
              <Dropdown
                v-model="form.state"
                :options="stateOptions"
                optionLabel="label"
                optionValue="value"
                class="tw-w-full"
                placeholder="—"
                showClear
              />
            </div>
            <div>
              <label class="field-label">City</label>
              <InputText v-model="form.city" class="tw-w-full" placeholder="Los Angeles" />
            </div>
            <div>
              <label class="field-label">County</label>
              <InputText v-model="form.county" class="tw-w-full" placeholder="Orange County" />
            </div>
            <div>
              <label class="field-label">ZIP</label>
              <InputText v-model="form.zip" class="tw-w-full" placeholder="90210" />
            </div>
            <div class="board-span">
              <label class="field-label">Logo URL</label>
              <InputText v-model="form.mlsLogoUrl" class="tw-w-full" placeholder="https://…/logo.png" />
              <div v-if="form.mlsLogoUrl" class="logo-preview tw-mt-2">
                <img :src="form.mlsLogoUrl" alt="" @error="onLogoError" />
                <span class="tw-text-muted tw-text-xs">Preview</span>
              </div>
            </div>
            <div class="board-span">
              <label class="field-label">Credential ref (env secret name only)</label>
              <InputText v-model="form.credentialRef" class="tw-w-full" placeholder="TRESTLE_CRMLS_TOKEN" />
            </div>
            <div class="board-span">
              <label class="field-label">Attribution</label>
              <InputText v-model="form.attributionText" class="tw-w-full" placeholder="Listing courtesy of …" />
            </div>
            <div class="board-span">
              <label class="field-label">Disclaimer</label>
              <Textarea v-model="form.disclaimerText" rows="2" class="tw-w-full" autoResize />
            </div>
            <div class="board-span">
              <label class="field-label">Notes</label>
              <Textarea v-model="form.notes" rows="2" class="tw-w-full" autoResize />
            </div>
          </div>
          <div class="tw-flex tw-gap-2 tw-mt-3">
            <Button type="submit" :label="editingId ? 'Save changes' : 'Add board'" icon="pi pi-check" :loading="saving" />
            <Button
              v-if="editingId"
              type="button"
              label="Cancel edit"
              class="p-button-outlined"
              icon="pi pi-times"
              @click="resetForm"
            />
          </div>
          <Message
            v-if="formMessage"
            :severity="formOk ? 'success' : 'error'"
            :closable="true"
            class="tw-mt-3"
            @close="formMessage = null"
          >
            {{ formMessage }}
          </Message>
        </form>
      </template>
    </Card>

    <Card>
      <template #title>Boards directory</template>
      <template #subtitle>
        Seed loads the full RESO US MLS list (~490 boards) as <strong>draft</strong>.
        Ranked by estimated member/subscriber count (published figures where known; heuristic otherwise).
      </template>
      <template #content>
        <div class="tw-mb-3 filter-bar">
          <div class="filter-fields">
            <div>
              <label class="field-label">Search</label>
              <InputText
                v-model="q"
                placeholder="name, acronym, city…"
                class="search-input"
                @input="onFilterInput"
                @keydown.enter.native.prevent="applyFiltersNow"
              />
            </div>
            <div>
              <label class="field-label">State</label>
              <Dropdown
                v-model="stateFilter"
                :options="stateFilterOptions"
                optionLabel="label"
                optionValue="value"
                class="geo-filter"
                filter
                showClear
                placeholder="All states"
                @change="applyFiltersNow"
              />
            </div>
            <div>
              <label class="field-label">City</label>
              <InputText
                v-model="cityFilter"
                placeholder="City"
                class="geo-input"
                @input="onFilterInput"
                @keydown.enter.native.prevent="applyFiltersNow"
              />
            </div>
            <div>
              <label class="field-label">County</label>
              <InputText
                v-model="countyFilter"
                placeholder="County"
                class="geo-input"
                @input="onFilterInput"
                @keydown.enter.native.prevent="applyFiltersNow"
              />
            </div>
            <div>
              <label class="field-label">ZIP</label>
              <InputText
                v-model="zipFilter"
                placeholder="ZIP"
                class="zip-input"
                @input="onFilterInput"
                @keydown.enter.native.prevent="applyFiltersNow"
              />
            </div>
            <div>
              <label class="field-label">Sort</label>
              <Dropdown
                v-model="sortBy"
                :options="sortOptions"
                optionLabel="label"
                optionValue="value"
                class="status-filter"
                @change="applyFiltersNow"
              />
            </div>
            <div>
              <label class="field-label">Status</label>
              <Dropdown
                v-model="statusFilter"
                :options="statusFilterOptions"
                optionLabel="label"
                optionValue="value"
                class="status-filter"
                @change="load"
              />
            </div>
            <div class="filter-actions">
              <Button label="Apply" icon="pi pi-filter" :loading="loading" @click="applyFiltersNow" />
              <Button
                label="Clear"
                icon="pi pi-times"
                class="p-button-outlined"
                :disabled="!hasActiveFilters"
                @click="clearFilters"
              />
              <Button label="Refresh" icon="pi pi-refresh" class="p-button-outlined" :loading="loading" @click="load" />
            </div>
          </div>
          <div class="tw-flex tw-gap-2">
            <Button
              label="Seed US MLS catalog"
              icon="pi pi-database"
              class="p-button-outlined"
              :loading="seeding"
              :disabled="!warehouseConfigured"
              @click="seedCatalog"
            />
          </div>
        </div>

        <Message v-if="error" severity="error" :closable="false" class="tw-mb-3">{{ error }}</Message>
        <Message v-if="seedMessage" :severity="seedOk ? 'success' : 'error'" :closable="true" class="tw-mb-3" @close="seedMessage = null">
          {{ seedMessage }}
        </Message>

        <DataTable :value="feeds" :paginator="true" :rows="25" responsiveLayout="scroll" class="boards-table" emptyMessage="No boards match these filters.">
          <Column header="#" style="width: 3.5rem" :sortable="false">
            <template #body="{ data }">
              <span
                class="rank-badge"
                :data-tier="rankTier(data.popularityRank)"
                :title="rankTitle(data)"
              >{{ data.popularityRank != null ? `#${data.popularityRank}` : '—' }}</span>
            </template>
          </Column>
          <Column header="" style="width: 3.25rem">
            <template #body="{ data }">
              <div class="board-logo">
                <img v-if="data.mlsLogoUrl" :src="data.mlsLogoUrl" :alt="data.displayName" @error="hideBrokenLogo" />
                <span v-else class="board-logo-fallback">{{ (data.mlsBoardAcronym || data.displayName || '?').slice(0, 2) }}</span>
              </div>
            </template>
          </Column>
          <Column field="displayName" header="Board" :sortable="true">
            <template #body="{ data }">
              <div class="board-name">{{ data.displayName }}</div>
              <div class="board-meta">{{ data.mlsBoardAcronym || '—' }} · {{ data.slug }}</div>
            </template>
          </Column>
          <Column field="estimatedMembers" header="Members" :sortable="true">
            <template #body="{ data }">
              <div class="board-name">{{ formatMembers(data.estimatedMembers) }}</div>
              <div class="board-meta">{{ confidenceLabel(data.popularityConfidence) }}</div>
            </template>
          </Column>
          <Column field="state" header="Location" :sortable="true">
            <template #body="{ data }">
              <div class="board-name">{{ locationLine(data) }}</div>
              <div v-if="data.county || data.zip" class="board-meta">
                <span v-if="data.county">{{ data.county }}</span>
                <span v-if="data.county && data.zip"> · </span>
                <span v-if="data.zip">{{ data.zip }}</span>
              </div>
            </template>
          </Column>
          <Column field="vendor" header="Vendor" :sortable="true" />
          <Column field="status" header="Status" :sortable="true">
            <template #body="{ data }">
              <span class="status-pill" :data-status="data.status">{{ data.status }}</span>
            </template>
          </Column>
          <Column field="credentialRef" header="Credential ref">
            <template #body="{ data }">
              <code v-if="data.credentialRef">{{ data.credentialRef }}</code>
              <span v-else class="tw-text-muted">—</span>
            </template>
          </Column>
          <Column header="Actions" style="width: 14rem">
            <template #body="{ data }">
              <div class="tw-flex tw-gap-1 tw-flex-wrap">
                <Button icon="pi pi-pencil" class="p-button-text p-button-sm" title="Edit" @click="edit(data)" />
                <Button
                  v-if="data.status !== 'active'"
                  icon="pi pi-play"
                  class="p-button-text p-button-sm p-button-success"
                  title="Set active"
                  @click="setStatus(data, 'active')"
                />
                <Button
                  v-if="data.status === 'active'"
                  icon="pi pi-pause"
                  class="p-button-text p-button-sm"
                  title="Pause"
                  @click="setStatus(data, 'paused')"
                />
                <Button
                  icon="pi pi-trash"
                  class="p-button-text p-button-sm p-button-danger"
                  title="Delete or retire"
                  @click="remove(data)"
                />
              </div>
            </template>
          </Column>
        </DataTable>
      </template>
    </Card>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import Card from 'primevue/card'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import Dropdown from 'primevue/dropdown'
import Message from 'primevue/message'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'

const FEEDS_QUERY = gql`
  query WarehouseFeeds(
    $includeDemo: Boolean
    $status: FeedStatus
    $q: String
    $state: String
    $city: String
    $county: String
    $zip: String
    $sortBy: String
  ) {
    listingsWarehouseStatus { configured }
    warehouseFeeds(
      includeDemo: $includeDemo
      status: $status
      q: $q
      state: $state
      city: $city
      county: $county
      zip: $zip
      sortBy: $sortBy
    ) {
      id slug displayName vendor mlsBoardName mlsBoardAcronym status
      credentialRef attributionText disclaimerText mlsLogoUrl notes
      state city county zip
      popularityRank estimatedMembers popularitySource popularityConfidence
      createdAt updatedAt
    }
  }
`

const SAVE_MUTATION = gql`
  mutation SaveWarehouseFeed($input: WarehouseFeedInput!) {
    saveWarehouseFeed(input: $input) {
      id slug displayName vendor status
    }
  }
`

const STATUS_MUTATION = gql`
  mutation SetWarehouseFeedStatus($id: ID!, $status: FeedStatus!) {
    setWarehouseFeedStatus(id: $id, status: $status) { id status }
  }
`

const DELETE_MUTATION = gql`
  mutation DeleteWarehouseFeed($id: ID!) {
    deleteWarehouseFeed(id: $id) {
      hardDeleted
      connectionCount
      feed { id status displayName }
    }
  }
`

const SEED_MUTATION = gql`
  mutation SeedUsMlsCatalog($onlyMissing: Boolean) {
    seedUsMlsCatalog(onlyMissing: $onlyMissing) {
      catalogCount inserted updated skipped onlyMissing
    }
  }
`

const US_STATES = [
  ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'],
  ['CA', 'California'], ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'],
  ['DC', 'District of Columbia'], ['FL', 'Florida'], ['GA', 'Georgia'], ['HI', 'Hawaii'],
  ['ID', 'Idaho'], ['IL', 'Illinois'], ['IN', 'Indiana'], ['IA', 'Iowa'],
  ['KS', 'Kansas'], ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'],
  ['MD', 'Maryland'], ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'],
  ['MS', 'Mississippi'], ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'],
  ['NV', 'Nevada'], ['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'],
  ['NY', 'New York'], ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'],
  ['OK', 'Oklahoma'], ['OR', 'Oregon'], ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'],
  ['SC', 'South Carolina'], ['SD', 'South Dakota'], ['TN', 'Tennessee'], ['TX', 'Texas'],
  ['UT', 'Utah'], ['VT', 'Vermont'], ['VA', 'Virginia'], ['WA', 'Washington'],
  ['WV', 'West Virginia'], ['WI', 'Wisconsin'], ['WY', 'Wyoming'],
]

function blankForm() {
  return {
    displayName: '',
    slug: '',
    mlsBoardName: '',
    mlsBoardAcronym: '',
    vendor: 'other',
    status: 'draft',
    credentialRef: '',
    mlsLogoUrl: '',
    state: null,
    city: '',
    county: '',
    zip: '',
    attributionText: '',
    disclaimerText: '',
    notes: '',
  }
}

export default {
  name: 'BoardsPage',
  components: { Card, Button, InputText, Textarea, Dropdown, Message, DataTable, Column },
  data() {
    return {
      feeds: [],
      allFeeds: [],
      loading: false,
      saving: false,
      seeding: false,
      error: null,
      formMessage: null,
      formOk: false,
      seedMessage: null,
      seedOk: false,
      warehouseConfigured: false,
      editingId: null,
      form: blankForm(),
      q: '',
      statusFilter: null,
      stateFilter: null,
      cityFilter: '',
      countyFilter: '',
      zipFilter: '',
      sortBy: 'popularity',
      filterTimer: null,
      sortOptions: [
        { label: 'Most popular', value: 'popularity' },
        { label: 'Name A–Z', value: 'name' },
      ],
      stateOptions: US_STATES.map(([value, label]) => ({ value, label: `${value} — ${label}` })),
      stateFilterOptions: [
        { label: 'All states', value: null },
        ...US_STATES.map(([value, label]) => ({ value, label: `${value} — ${label}` })),
      ],
      vendorOptions: [
        { label: 'MLS Grid', value: 'mls_grid' },
        { label: 'Trestle', value: 'trestle' },
        { label: 'Bridge Interactive', value: 'bridge_interactive' },
        { label: 'RMLS', value: 'rmls' },
        { label: 'Other', value: 'other' },
      ],
      statusOptions: [
        { label: 'Draft', value: 'draft' },
        { label: 'Active', value: 'active' },
        { label: 'Paused', value: 'paused' },
        { label: 'Error', value: 'error' },
        { label: 'Retired', value: 'retired' },
      ],
      statusFilterOptions: [
        { label: 'All', value: null },
        { label: 'Draft', value: 'draft' },
        { label: 'Active', value: 'active' },
        { label: 'Paused', value: 'paused' },
        { label: 'Error', value: 'error' },
        { label: 'Retired', value: 'retired' },
      ],
    }
  },
  mounted() {
    this.load()
  },
  beforeDestroy() {
    if (this.filterTimer) clearTimeout(this.filterTimer)
  },
  computed: {
    hasActiveFilters() {
      return Boolean(
        this.q ||
        this.statusFilter ||
        this.stateFilter ||
        this.cityFilter ||
        this.countyFilter ||
        this.zipFilter
      )
    },
  },
  methods: {
    countByStatus(status) {
      return this.allFeeds.filter(f => f.status === status).length
    },
    matchesNeedle(value, needle) {
      return Boolean(value && String(value).toLowerCase().includes(needle))
    },
    onFilterInput() {
      if (this.filterTimer) clearTimeout(this.filterTimer)
      this.filterTimer = setTimeout(() => {
        this.applyFiltersNow()
      }, 120)
    },
    applyFiltersNow() {
      if (this.filterTimer) {
        clearTimeout(this.filterTimer)
        this.filterTimer = null
      }
      this.feeds = this.filterAndSortFeeds(this.allFeeds)
    },
    filterAndSortFeeds(rows) {
      let next = Array.isArray(rows) ? rows.slice() : []
      const q = String(this.q || '').trim().toLowerCase()
      const state = this.stateFilter ? String(this.stateFilter).toUpperCase() : null
      const city = String(this.cityFilter || '').trim().toLowerCase()
      const county = String(this.countyFilter || '').trim().toLowerCase()
      const zip = String(this.zipFilter || '').trim().toLowerCase()

      if (state) {
        next = next.filter(r => String(r.state || '').toUpperCase() === state)
      }
      if (city) {
        next = next.filter(r => this.matchesNeedle(r.city, city))
      }
      if (county) {
        next = next.filter(r => this.matchesNeedle(r.county, county))
      }
      if (zip) {
        next = next.filter(r => this.matchesNeedle(r.zip, zip))
      }
      if (q) {
        next = next.filter(r => (
          this.matchesNeedle(r.displayName, q) ||
          this.matchesNeedle(r.mlsBoardName, q) ||
          this.matchesNeedle(r.mlsBoardAcronym, q) ||
          this.matchesNeedle(r.slug, q) ||
          this.matchesNeedle(r.vendor, q) ||
          this.matchesNeedle(r.state, q) ||
          this.matchesNeedle(r.city, q) ||
          this.matchesNeedle(r.county, q) ||
          this.matchesNeedle(r.zip, q) ||
          this.matchesNeedle(r.notes, q)
        ))
      }

      if (this.sortBy === 'name') {
        next.sort((a, b) => String(a.displayName || '').localeCompare(String(b.displayName || '')))
      } else {
        next.sort((a, b) => {
          const ar = a.popularityRank == null ? Number.POSITIVE_INFINITY : a.popularityRank
          const br = b.popularityRank == null ? Number.POSITIVE_INFINITY : b.popularityRank
          if (ar !== br) return ar - br
          const am = a.estimatedMembers == null ? -1 : a.estimatedMembers
          const bm = b.estimatedMembers == null ? -1 : b.estimatedMembers
          if (am !== bm) return bm - am
          return String(a.displayName || '').localeCompare(String(b.displayName || ''))
        })
      }
      return next
    },
    locationLine(row) {
      const parts = [row.city, row.state].filter(Boolean)
      return parts.length ? parts.join(', ') : '—'
    },
    formatMembers(n) {
      if (n == null || n === '') return '—'
      const num = Number(n)
      if (!Number.isFinite(num)) return '—'
      if (num >= 1000) return `~${Math.round(num / 100) / 10}k`
      return `~${num}`
    },
    confidenceLabel(c) {
      if (c === 'published') return 'Published estimate'
      if (c === 'estimate') return 'Named estimate'
      if (c === 'heuristic') return 'Heuristic'
      return c || '—'
    },
    rankTier(rank) {
      if (rank == null) return 'none'
      if (rank <= 10) return 'top'
      if (rank <= 50) return 'high'
      if (rank <= 150) return 'mid'
      return 'low'
    },
    rankTitle(row) {
      const bits = []
      if (row.popularityRank != null) bits.push(`US popularity #${row.popularityRank}`)
      if (row.estimatedMembers != null) bits.push(`~${Number(row.estimatedMembers).toLocaleString()} members`)
      if (row.popularitySource) bits.push(row.popularitySource)
      return bits.join(' · ') || 'No rank yet'
    },
    hideBrokenLogo(e) {
      if (e && e.target) e.target.style.display = 'none'
    },
    onLogoError(e) {
      if (e && e.target) e.target.style.opacity = '0.25'
    },
    clearFilters() {
      this.q = ''
      this.statusFilter = null
      this.stateFilter = null
      this.cityFilter = ''
      this.countyFilter = ''
      this.zipFilter = ''
      this.sortBy = 'popularity'
      this.load()
    },
    async load() {
      this.loading = true
      this.error = null
      try {
        const { data } = await this.$apollo.query({
          query: FEEDS_QUERY,
          variables: {
            includeDemo: false,
            status: this.statusFilter || null,
            q: null,
            state: null,
            city: null,
            county: null,
            zip: null,
            sortBy: this.sortBy || 'popularity',
          },
          fetchPolicy: 'network-only',
        })
        this.warehouseConfigured = Boolean(data.listingsWarehouseStatus && data.listingsWarehouseStatus.configured)
        this.allFeeds = data.warehouseFeeds || []
        this.applyFiltersNow()
      } catch (err) {
        this.error = (err && err.message) || 'Failed to load boards'
        this.allFeeds = []
        this.feeds = []
        if (/not configured/i.test(this.error)) {
          this.warehouseConfigured = false
        }
      } finally {
        this.loading = false
      }
    },
    resetForm() {
      this.editingId = null
      this.form = blankForm()
      this.formMessage = null
    },
    edit(row) {
      this.editingId = row.id
      this.form = {
        displayName: row.displayName || '',
        slug: row.slug || '',
        mlsBoardName: row.mlsBoardName || '',
        mlsBoardAcronym: row.mlsBoardAcronym || '',
        vendor: row.vendor || 'other',
        status: row.status || 'draft',
        credentialRef: row.credentialRef || '',
        mlsLogoUrl: row.mlsLogoUrl || '',
        state: row.state || null,
        city: row.city || '',
        county: row.county || '',
        zip: row.zip || '',
        attributionText: row.attributionText || '',
        disclaimerText: row.disclaimerText || '',
        notes: row.notes || '',
      }
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    async save() {
      this.saving = true
      this.formMessage = null
      try {
        const input = {
          displayName: this.form.displayName,
          mlsBoardName: this.form.mlsBoardName || this.form.displayName,
          mlsBoardAcronym: this.form.mlsBoardAcronym || null,
          vendor: this.form.vendor,
          status: this.form.status,
          credentialRef: this.form.credentialRef || null,
          mlsLogoUrl: this.form.mlsLogoUrl || null,
          state: this.form.state || null,
          city: this.form.city || null,
          county: this.form.county || null,
          zip: this.form.zip || null,
          attributionText: this.form.attributionText || null,
          disclaimerText: this.form.disclaimerText || null,
          notes: this.form.notes || null,
        }
        if (this.editingId) {
          input.id = this.editingId
        } else {
          input.slug = this.form.slug || this.form.displayName
        }
        await this.$apollo.mutate({
          mutation: SAVE_MUTATION,
          variables: { input },
        })
        this.formOk = true
        this.formMessage = this.editingId ? 'Board updated.' : 'Board added — Brokurz can list it now.'
        this.resetForm()
        await this.load()
      } catch (err) {
        this.formOk = false
        this.formMessage = (err && err.message) || 'Save failed'
      } finally {
        this.saving = false
      }
    },
    async setStatus(row, status) {
      try {
        await this.$apollo.mutate({
          mutation: STATUS_MUTATION,
          variables: { id: row.id, status },
        })
        await this.load()
      } catch (err) {
        this.error = (err && err.message) || 'Status update failed'
      }
    },
    async remove(row) {
      const ok = window.confirm(
        `Remove “${row.displayName}”? If brokerages are connected it will be retired instead of deleted.`
      )
      if (!ok) return
      try {
        const { data } = await this.$apollo.mutate({
          mutation: DELETE_MUTATION,
          variables: { id: row.id },
        })
        const result = data.deleteWarehouseFeed
        if (result.hardDeleted) {
          this.formOk = true
          this.formMessage = 'Board deleted.'
        } else {
          this.formOk = true
          this.formMessage = `Board retired (${result.connectionCount} connection(s) still referenced it).`
        }
        await this.load()
      } catch (err) {
        this.error = (err && err.message) || 'Delete failed'
      }
    },
    async seedCatalog() {
      const ok = window.confirm(
        'Seed the US MLS catalog into feeds as draft boards? Existing slugs are left alone.'
      )
      if (!ok) return
      this.seeding = true
      this.seedMessage = null
      try {
        const { data } = await this.$apollo.mutate({
          mutation: SEED_MUTATION,
          variables: { onlyMissing: true },
        })
        const r = data.seedUsMlsCatalog
        this.seedOk = true
        this.seedMessage = `Catalog ${r.catalogCount}: inserted ${r.inserted}, skipped ${r.skipped}, updated ${r.updated}.`
        await this.load()
      } catch (err) {
        this.seedOk = false
        this.seedMessage = (err && err.message) || 'Seed failed'
      } finally {
        this.seeding = false
      }
    },
  },
}
</script>

<style scoped>
.kpi-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.75rem;
}
.kpi-card {
  border: 1px solid var(--bk-line);
  border-radius: var(--bk-radius-sm);
  background: #fff;
  padding: 0.85rem 1rem;
}
.kpi-label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--bk-muted);
}
.kpi-value {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.2;
}
.kpi-hint {
  font-size: 0.75rem;
  color: var(--bk-muted);
}
.field-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}
.board-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem 1rem;
}
.board-span {
  grid-column: 1 / -1;
}
.board-name {
  font-weight: 600;
}
.board-meta {
  font-size: 0.75rem;
  color: var(--bk-muted);
}
.board-logo {
  width: 2rem;
  height: 2rem;
  border-radius: 6px;
  border: 1px solid var(--bk-line);
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.board-logo img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.board-logo-fallback {
  font-size: 0.65rem;
  font-weight: 700;
  color: var(--bk-muted);
  text-transform: uppercase;
}
.logo-preview {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.logo-preview img {
  width: 2rem;
  height: 2rem;
  object-fit: contain;
  border: 1px solid var(--bk-line);
  border-radius: 6px;
  background: #fff;
}
.search-input {
  min-width: 220px;
}
.geo-filter {
  min-width: 11rem;
}
.geo-input {
  width: 8.5rem;
}
.zip-input {
  width: 6rem;
}
.status-filter {
  min-width: 140px;
}
.filter-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: flex-end;
  justify-content: space-between;
}
.filter-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 0.75rem;
  align-items: flex-end;
}
.filter-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  align-items: center;
  padding-bottom: 0.1rem;
}
.rank-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2.4rem;
  padding: 0.15rem 0.35rem;
  border-radius: 6px;
  font-size: 0.72rem;
  font-weight: 700;
  border: 1px solid var(--bk-line);
  color: var(--bk-muted);
  background: #f8f8f8;
}
.rank-badge[data-tier='top'] {
  color: #7a4a00;
  background: #fff4df;
  border-color: #f0d29a;
}
.rank-badge[data-tier='high'] {
  color: #1f4b7a;
  background: #eaf3fb;
  border-color: #b9d4ec;
}
.rank-badge[data-tier='mid'] {
  color: #355a3a;
  background: #eef6ef;
  border-color: #c5dec8;
}
.status-pill {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  background: #f3f4f6;
}
.status-pill[data-status='active'] {
  background: #dcfce7;
  color: #166534;
}
.status-pill[data-status='draft'] {
  background: #e0e7ff;
  color: #3730a3;
}
.status-pill[data-status='paused'] {
  background: #fef3c7;
  color: #92400e;
}
.status-pill[data-status='retired'],
.status-pill[data-status='error'] {
  background: #fee2e2;
  color: #991b1b;
}
@media (max-width: 720px) {
  .board-grid {
    grid-template-columns: 1fr;
  }
}
</style>
